import type { OptimizedMessage } from '../services/api.ts';
import type { MessageLearningAction } from '../services/agentLearning.ts';
import { reconcileMessageDelivery } from './messageDelivery.ts';
export { mergeMessageDelivery } from './messageDelivery.ts';
export type { MessageDeliveryState } from './messageDelivery.ts';

export const messageAuthorLabel = (message: OptimizedMessage): string | null => {
  switch (message.author_type) {
    case 'patient': return 'Paciente';
    case 'agent': return 'Agente IA';
    case 'platform_human': {
      const name = message.author_name?.trim();
      return name ? `${name} · Usuário da plataforma` : 'Usuário da plataforma';
    }
    case 'connected_whatsapp_human': return 'WhatsApp conectado';
    case 'automation': return 'Automação';
    default: return null;
  }
};

/** A ação histórica vem do servidor; caches antigos só aceitam o recibo já comprovado. */
export const messageLearningAction = (message: OptimizedMessage): MessageLearningAction | null => {
  if (message.learning_action !== undefined) return message.learning_action;
  return message.author_type === 'agent' && message.author_evidence === 'agent_delivery' ? 'correction' : null;
};

export const canCorrectAgentMessage = (
  message: OptimizedMessage,
  canManage: boolean,
  contactId: number,
): boolean => {
  const action = messageLearningAction(message);
  const allowedAuthor = action === 'correction'
    ? message.author_type === 'agent' && (message.author_evidence === 'agent_delivery' || message.author_evidence === 'legacy_agent_delivery')
    : action === 'instruction' && !message.author_type;
  return Boolean(
  canManage &&
  Number.isInteger(contactId) && contactId > 0 &&
  allowedAuthor &&
  message.fromMe === true &&
  message.source_channel !== 'instagram' &&
  (message.type === 'audio' || (
    message.type === 'text' &&
    typeof message.content === 'string' && message.content.trim()
  )) &&
  Number.isInteger(message.databaseMessageId) && Number(message.databaseMessageId) > 0 &&
  String(message.databaseMessageId) === message.id &&
  /^[1-9]\d*$/.test(message.id),
  );
};

/** Permite eco humano confirmado pelo servidor sem reabrir o fluxo de duplicatas do dispositivo. */
export const shouldDisplayOutgoingWebSocketMessage = (message: {
  fromMe?: boolean;
  fromApi?: boolean;
  source_channel?: string;
  author_type?: string | null;
  dbMessageId?: number | string | null;
}): boolean => {
  if (!message.fromMe || message.fromApi || message.source_channel === 'instagram') return true;
  const id = Number(message.dbMessageId);
  return Number.isInteger(id) && id > 0 &&
    (message.author_type === 'platform_human' || message.author_type === 'connected_whatsapp_human');
};

/** Preserva a identidade persistida e autoria confirmada ao reconciliar REST, cache e eco WS. */
export const reconcileConfirmedMessage = (
  existing: OptimizedMessage,
  incoming: OptimizedMessage,
): OptimizedMessage => {
  return {
    ...reconcileMessageDelivery(existing, incoming),
    author_type: incoming.author_type ?? existing.author_type,
    author_id: incoming.author_id ?? existing.author_id,
    author_name: incoming.author_name ?? existing.author_name,
    author_evidence: incoming.author_evidence ?? existing.author_evidence,
    agent_run_id: incoming.agent_run_id ?? existing.agent_run_id,
    learning_action: incoming.learning_action !== undefined ? incoming.learning_action : existing.learning_action,
  };
};
